/**
 * Page access bundles — assign one id (stored in user.pageAccess) to grant every route in the bundle.
 * Used by pageAccessMatch, PageAccessControl, bulk assign, and hub visibility.
 */

export interface PageAccessBundle {
  /** Stored in user.pageAccess (e.g. '/app/workspace'). */
  id: string;
  label: string;
  description: string;
  /** Human-readable list shown in the access editor. */
  includes: string[];
}

/** Service orders, clients, catalog, and team routing (chat topics). */
export function matchesOrdersDashboardBundle(currentPath: string): boolean {
  if (currentPath === '/app/orders-dashboard' || currentPath.startsWith('/app/orders-dashboard/')) {
    return true;
  }
  if (currentPath.startsWith('/app/service-orders')) return true;
  if (currentPath.startsWith('/app/so/')) return true;
  if (currentPath.startsWith('/app/my-cases')) return true;
  if (currentPath.startsWith('/app/manage-services')) return true;
  if (currentPath.startsWith('/app/chat-topics')) return true;
  if (currentPath.startsWith('/app/clients')) return true;
  if (currentPath.startsWith('/app/conversions')) return true;
  if (currentPath.startsWith('/app/forms-management')) return true;
  if (currentPath.startsWith('/app/whatsapp-settings')) return true;
  if (currentPath.startsWith('/app/reports')) return true;
  if (currentPath.startsWith('/app/legal-tasks')) return true;
  if (currentPath.startsWith('/app/shipping')) return true;
  return false;
}

/** Staff mailbox inbox and thread views. */
export function matchesMailboxBundle(currentPath: string): boolean {
  return currentPath === '/app/mailbox' || currentPath.startsWith('/app/mailbox/');
}

/** Dev tools — not HR people admin, offices, or operational catalog. */
export function matchesWorkspaceBundle(currentPath: string): boolean {
  if (currentPath === '/app/workspace') return true;
  if (currentPath === '/app/workspace-settings') return true;
  if (currentPath.startsWith('/app/workspace/')) {
    if (currentPath === '/app/workspace/page-access' || currentPath.startsWith('/app/workspace/page-access/')) {
      return false;
    }
    return true;
  }
  return false;
}

/** HR & Admin: users, offices, department org chart, profiles, attendance, training, workload, access matrix. */
export function matchesWorkspaceHrBundle(currentPath: string): boolean {
  if (currentPath.startsWith('/app/manage-users')) return true;
  if (currentPath.startsWith('/app/hr-profiles')) return true;
  if (currentPath.startsWith('/app/hr-attendance')) return true;
  if (currentPath.startsWith('/app/hr-training')) return true;
  if (currentPath.startsWith('/app/hr-policy')) return true;
  if (currentPath.startsWith('/app/departments')) return true;
  if (currentPath.startsWith('/app/users/')) return true;
  if (currentPath.startsWith('/app/workload')) return true;
  if (currentPath.startsWith('/app/office-management')) return true;
  if (currentPath === '/app/workspace/page-access' || currentPath.startsWith('/app/workspace/page-access/')) {
    return true;
  }
  return false;
}

export const PAGE_ACCESS_BUNDLES: PageAccessBundle[] = [
  {
    id: '/app/orders-dashboard',
    label: 'Ops Dashboard',
    description: 'Cases, clients, service catalog, and team routing',
    includes: [
      'Order list, conversions, by service, by case manager, Canada / India',
      'Clients, order collection',
      'Operational setup: forms, service rates, manage services, chat topics, Quick Message',
      'Reports (daily work, conversion log, walk-ins by agent/office)',
      'Task Manager (team task board)',
      'My Cases',
    ],
  },
  {
    id: '/app/workspace',
    label: 'Workspace',
    description: 'Dev / IT tools and workspace hub',
    includes: [
      'Workspace hub',
      'CWY Token Dashboard',
      'Sign-in logs, page usage, sessions, alerts, AI management (admin UI)',
    ],
  },
  {
    id: '/app/workspace-hr',
    label: 'Workspace — HR',
    description: 'People admin — matches your HR & Admin department',
    includes: [
      'Manage users & user detail pages',
      'Department management (Case, Filing, HR, IT, Legal, Sales…)',
      'Office management',
      'HR Documents',
      'Attendance Board',
      'Training Center',
      'HR Policy / Work Compliance',
      'Workload & names by member',
      'Page access matrix',
    ],
  },
  {
    id: '/app/mailbox',
    label: 'Mailbox',
    description: 'Staff application-hosted inbox',
    includes: ['Inbox list', 'Thread view', 'Compose and reply'],
  },
];

const BUNDLE_MATCHERS: Record<string, (path: string) => boolean> = {
  '/app/mailbox': matchesMailboxBundle,
  '/app/orders-dashboard': matchesOrdersDashboardBundle,
  '/app/orders-dashboard/my-cases': matchesOrdersDashboardBundle,
  '/app/workspace': matchesWorkspaceBundle,
  '/app/workspace-settings': matchesWorkspaceBundle,
  '/app/workspace-hr': matchesWorkspaceHrBundle,
};

/** Stored in user.pageAccess — Workspace (dev/IT) bundle ids only. */
export const WORKSPACE_OPS_GRANT_IDS = new Set(['/app/workspace', '/app/workspace-settings']);

/** Stored in user.pageAccess — Workspace — HR bundle or legacy HR page ids. */
export const WORKSPACE_HR_GRANT_IDS = new Set([
  '/app/workspace-hr',
  '/app/manage-users',
  '/app/hr-attendance',
  '/app/hr-training',
  '/app/hr-policy',
  '/app/hr-profiles',
  '/app/departments',
  '/app/office-management',
  '/app/workload',
  '/app/workspace/page-access',
]);

/** True when pageAccess contains only Workspace ops grants (no HR bundle or legacy HR paths). */
export function hasOnlyWorkspaceOpsGrants(pageAccess: string[]): boolean {
  if (!pageAccess.length) return false;
  return pageAccess.every((entry) => WORKSPACE_OPS_GRANT_IDS.has(entry));
}

/**
 * When Workspace — HR is not assigned, drop legacy HR page ids so Workspace-only users
 * are not left with stray manage-users / hr-attendance entries from old defaults.
 */
export function normalizePageAccessForSave(pageAccess: string[]): string[] {
  if (!pageAccess.includes('/app/workspace-hr')) {
    return pageAccess.filter((entry) => !WORKSPACE_HR_GRANT_IDS.has(entry));
  }
  return pageAccess;
}

export function isPathCoveredByBundleGrant(grant: string, currentPath: string): boolean {
  const matcher = BUNDLE_MATCHERS[grant];
  return matcher ? matcher(currentPath) : false;
}

export function getPageAccessBundle(grant: string): PageAccessBundle | undefined {
  return PAGE_ACCESS_BUNDLES.find((b) => b.id === grant);
}

export function getPageAccessBundleOptions(): Array<{ path: string; label: string }> {
  return PAGE_ACCESS_BUNDLES.map((b) => ({ path: b.id, label: b.label }));
}

/** Representative route for filters / “does user have this bundle?” */
export function getBundleProbePath(bundleOrPagePath: string): string {
  if (bundleOrPagePath === '/app/workspace-hr') return '/app/departments';
  if (bundleOrPagePath === '/app/workspace') return '/app/workspace/cwy-token';
  if (bundleOrPagePath === '/app/orders-dashboard') return '/app/clients';
  return bundleOrPagePath;
}
